/**
 * CustomVariablesSection Component
 *
 * Accordion section for defining custom variables set by analytics scripts.
 * Part of the "DEFINE → USE" pattern: defines parameters that can be used in destinations.
 *
 * Features:
 * - Define variable keys that scripts can set via window.segmentlySDK.setCustomData()
 * - Specify data type for validation
 * - Optional default value
 *
 * SECURITY: Only explicitly defined variables are collected - scripts cannot
 * inject arbitrary data into destinations.
 *
 * @module webplacements/components/editors/sources
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ParameterDefinition } from '@modules/webplacements/types/webplacements.types';

export interface CustomVariablesSectionProps {
  /** Current custom variable parameters (filtered from all parameters) */
  parameters: ParameterDefinition[];
  /** Called when parameters change */
  onChange: (params: ParameterDefinition[]) => void;
  /** Whether custom analytics is configured */
  hasCustomAnalytics?: boolean;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

interface NewParamState {
  key: string;
  label: string;
  dataType: 'string' | 'number' | 'boolean';
  description: string;
}

const initialNewParam: NewParamState = {
  key: '',
  label: '',
  dataType: 'string',
  description: '',
};

/**
 * CustomVariablesSection Component
 *
 * Provides a table-based interface for defining custom variable parameters.
 * These variables are set by user scripts via window.segmentlySDK.setCustomData().
 */
export const CustomVariablesSection: React.FC<CustomVariablesSectionProps> = ({
  parameters,
  onChange,
  hasCustomAnalytics = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const [newParam, setNewParam] = useState<NewParamState>(initialNewParam);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddClick = useCallback(() => {
    setIsAdding(true);
    setNewParam(initialNewParam);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, []);

  const handleSaveNew = useCallback(() => {
    if (!newParam.key || !newParam.label) return;

    // Check for duplicate keys
    if (parameters.some((p) => p.key === newParam.key)) {
      return; // Could show error toast here
    }

    const newDefinition: ParameterDefinition = {
      key: newParam.key,
      source: 'custom',
      sourceDetails: {},
      label: newParam.label,
      dataType: newParam.dataType,
      // Only include description if it has a value (Firestore rejects undefined)
      ...(newParam.description && { description: newParam.description }),
    };

    onChange([...parameters, newDefinition]);
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, [newParam, parameters, onChange]);

  const handleDelete = useCallback(
    (key: string) => {
      onChange(parameters.filter((p) => p.key !== key));
    },
    [parameters, onChange]
  );

  const handleKeyChange = useCallback((key: string) => {
    const sanitizedKey = key.replace(/[^a-zA-Z0-9_]/g, '');
    // Auto-suggest label from key
    const suggestedLabel = sanitizedKey
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^\s+/, '')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    setNewParam((prev) => ({
      ...prev,
      key: sanitizedKey,
      label: prev.label || suggestedLabel,
    }));
  }, []);

  return (
    <Box>
      {/* Header with Add button on the right */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CodeIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
        <Typography variant="body2" color="text.secondary">
          Define variables that custom scripts can set via segmentlySDK
        </Typography>
        <Tooltip title="Custom variables are set by your analytics scripts using window.segmentlySDK.setCustomData(key, value). Only variables defined here can be used in destinations - this prevents unauthorized data injection.">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
        {/* Spacer to push button to the right */}
        <Box sx={{ flex: 1 }} />
        {!isAdding && (
          <Button
            data-testid="wp-custom-add-btn"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={disabled}
          >
            Add Custom Variable
          </Button>
        )}
      </Box>

      {/* Security Notice */}
      <Box
        sx={{
          p: 1.5,
          mb: 2,
          bgcolor: alpha(theme.palette.info.main, 0.06),
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          <strong>Security:</strong> Only explicitly defined variables can be collected.
          Use in scripts: <code>window.segmentlySDK.setCustomData('key', value)</code>
        </Typography>
      </Box>

      {/* Parameters Table */}
      {parameters.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Variable Key</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Label</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Type</TableCell>
                <TableCell align="right" sx={{ width: '20%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map((param, index) => (
                <TableRow key={param.key} hover data-testid={`wp-custom-row-${index}`}>
                  <TableCell>
                    <Chip
                      label={`{{${param.key}}}`}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: alpha(theme.palette.warning.main, 0.15),
                        color: theme.palette.warning.main,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{param.label}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={param.dataType}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      data-testid={`wp-custom-delete-${index}`}
                      size="small"
                      onClick={() => handleDelete(param.key)}
                      disabled={disabled}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add New Row */}
      {isAdding ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Vertical layout - one field per row */}
          <TextField
            label="Variable Key"
            value={newParam.key}
            onChange={(e) => handleKeyChange(e.target.value)}
            size="small"
            fullWidth
            required
            error={!newParam.key && newParam.label.length > 0}
            placeholder="lead_score"
            helperText="Key used in setCustomData()"
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
          />
          <TextField
            label="Display Label"
            value={newParam.label}
            onChange={(e) =>
              setNewParam((prev) => ({
                ...prev,
                label: e.target.value,
              }))
            }
            size="small"
            fullWidth
            placeholder="Lead Score"
            helperText="Human-readable name"
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Data Type</InputLabel>
            <Select
              value={newParam.dataType}
              onChange={(e) =>
                setNewParam((prev) => ({
                  ...prev,
                  dataType: e.target.value as 'string' | 'number' | 'boolean',
                }))
              }
              label="Data Type"
            >
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="number">Number</MenuItem>
              <MenuItem value="boolean">Boolean</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Description (optional)"
            value={newParam.description}
            onChange={(e) =>
              setNewParam((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            size="small"
            fullWidth
            placeholder="Calculated from user selections"
            helperText="Optional context for this variable"
          />

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button data-testid="wp-custom-cancel-btn" size="small" onClick={handleCancelAdd}>
              Cancel
            </Button>
            <Button
              data-testid="wp-custom-save-btn"
              size="small"
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newParam.key || !newParam.label}
            >
              Add
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* Empty State */}
      {parameters.length === 0 && !isAdding && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No custom variables defined yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {hasCustomAnalytics
              ? 'Define variables that your analytics script will set.'
              : 'Configure Custom Analytics script first, then define variables here.'}
          </Typography>
        </Box>
      )}

      {/* Usage Example */}
      {parameters.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            bgcolor: theme.palette.action.selected,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.75rem',
          }}
        >
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            Usage in your script:
          </Typography>
          <code>
            {parameters.map((p) => `window.segmentlySDK.setCustomData('${p.key}', value);`).join('\n')}
          </code>
        </Box>
      )}
    </Box>
  );
};

export default CustomVariablesSection;
